import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartSection } from '@/types';

interface ChartRendererProps {
  section: ChartSection;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export function ChartRenderer({ section }: ChartRendererProps) {
  const { title, chartType, labels, values } = section.data;

  // Validate data
  if (!labels || !Array.isArray(labels) || labels.length === 0) {
    return (
      <div className="p-6 bg-card border border-border rounded-lg">
        {title && <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>}
        <div className="text-red-500">Invalid chart data: labels is required</div>
      </div>
    );
  }

  if (!values || !Array.isArray(values)) {
    return (
      <div className="p-6 bg-card border border-border rounded-lg">
        {title && <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>}
        <div className="text-red-500">Invalid chart data: values is required</div>
      </div>
    );
  }

  // Transform data for recharts
  const data = labels.map((label, index) => ({
    name: label,
    value: values[index] || 0,
  }));

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
              <Legend />
              <Bar dataKey="value" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
              <Legend />
              <Area type="monotone" dataKey="value" stroke="#3B82F6" fill="#3B82F680" />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return <div className="text-red-500">Unsupported chart type: {chartType}</div>;
    }
  };

  return (
    <div className="p-6 bg-card border border-border rounded-lg">
      {title && <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>}
      {renderChart()}
    </div>
  );
}
